/**
 * HandScoreTracker - Hand-by-Hand Points & Scorecard Tracking System
 * Tracks points scored in each hand by all players:
 * - Points pegged
 * - Points in hand
 * - Points in crib
 * - Round total (pegged + hand + crib)
 * - Running total of match points
 * Generates an end-of-game summary scorecard modal with detailed hand breakdown.
 */

import { GameReplayManager } from '../game/GameReplayManager.js';
import { AvatarManager } from '../ui/AvatarManager.js';

export class HandScoreTracker {
  static hands = [];

  /**
   * Resets the tracked hands for a fresh match.
   */
  static reset() {
    HandScoreTracker.hands = [];
  }

  /**
   * Returns a copy of the tracked hands.
   * @returns {Array}
   */
  static getHands() {
    return [...HandScoreTracker.hands];
  }

  /**
   * Formats a card object into a compact mini-badge string.
   * @param {Object} card
   * @returns {string}
   */
  static formatMiniCard(card) {
    if (!card) return '—';
    const r = card.r || card.rank || '';
    const s = card.s || card.suit || '';
    const isRed = s === 'H' || s === 'D';
    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
    const sym = suitSyms[s] || s;
    const rankNames = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
    const rName = rankNames[r] || r;
    const textColor = isRed ? '#dc2626' : '#111111';
    return `<span class="sc-cut-card ${isRed ? 'sc-red red' : 'sc-black black'}" style="color:${textColor} !important; -webkit-text-fill-color:${textColor} !important; background:#ffffff !important;">${rName}${sym}</span>`;
  }

  /**
   * Records a completed hand from the show scoring phase.
   * @param {Object} state - Current GameState
   * @param {Object} settings - Active game settings
   * @param {Object} [options]
   * @returns {Object} The recorded hand record
   */
  static recordHand(state, settings = {}, options = {}) {
    if (!state) return null;
    const pName = options.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (s => s === 'you' ? 'You' : s === 'cpu' ? 'Computer' : s));
    const isPart = options.isPartnership || (typeof window !== 'undefined' && typeof window.isPartnership === 'function' ? window.isPartnership() : false);
    const count = settings.playerCount || 2;
    const handNum = HandScoreTracker.hands.length + 1;
    const ls = state.lastShow || {};

    const handRecord = {
      handNumber: handNum,
      dealer: state.dealer,
      dealerName: pName(state.dealer),
      starter: state.starter ? { ...state.starter } : null,
      scores: {},
      isPartnership: isPart,
      endedMidPlay: false
    };

    if (count === 2 && !isPart) {
      const youPeg = (state.handPeg && (state.handPeg.you ?? state.handPeg.p1)) || 0;
      const cpuPeg = (state.handPeg && (state.handPeg.cpu ?? state.handPeg.p2)) || 0;

      const dealerIsYou = state.dealer === 'you' || state.dealer === 'p1';
      const poneKey = dealerIsYou ? 'cpu' : 'you';
      const dealerKey = dealerIsYou ? 'you' : 'cpu';

      const poneScore = (ls.pone && ls.pone.score) || { total: 0 };
      const dealScore = (ls.dealer && ls.dealer.score) || { total: 0 };
      const cribSigned = ls.crib ? (ls.crib.signed ?? 0) : 0;

      const poneCounted = (ls.pone && ls.pone.blockedByZone) ? 0 : poneScore.total;
      const dealCounted = (ls.dealer && ls.dealer.blockedByZone) ? 0 : dealScore.total;
      const cribCounted = (ls.crib && ls.crib.blockedByZone) ? 0 : cribSigned;

      const cribRecipient = (ls.crib && ls.crib.who) || dealerKey;
      const youGotCrib = (cribRecipient === 'you' || cribRecipient === 'p1');

      const youHand = poneKey === 'you' ? poneCounted : dealCounted;
      const cpuHand = poneKey === 'cpu' ? poneCounted : dealCounted;

      const youCrib = youGotCrib ? cribCounted : 0;
      const cpuCrib = !youGotCrib ? cribCounted : 0;

      const youRound = youPeg + youHand + youCrib;
      const cpuRound = cpuPeg + cpuHand + cpuCrib;

      const youTotal = state.scores ? (state.scores.you ?? state.scores.p1 ?? 0) : 0;
      const cpuTotal = state.scores ? (state.scores.cpu ?? state.scores.p2 ?? 0) : 0;

      handRecord.scores['p1'] = {
        id: 'p1',
        name: pName('you'),
        isDealer: dealerIsYou,
        pegged: youPeg,
        hand: youHand,
        crib: youCrib,
        hasCrib: youGotCrib,
        roundTotal: youRound,
        runningTotal: youTotal
      };

      handRecord.scores['p2'] = {
        id: 'p2',
        name: pName('cpu'),
        isDealer: !dealerIsYou,
        pegged: cpuPeg,
        hand: cpuHand,
        crib: cpuCrib,
        hasCrib: !youGotCrib,
        roundTotal: cpuRound,
        runningTotal: cpuTotal
      };
    } else if (isPart) {
      // Partnership / Teams
      const t1Peg = (state.handPeg && state.handPeg.team1) || 0;
      const t2Peg = (state.handPeg && state.handPeg.team2) || 0;

      let t1Hand = 0, t2Hand = 0, t1Crib = 0, t2Crib = 0;
      const cribRecipient = (ls.crib && ls.crib.who) || state.dealer;
      const cribCounted = (ls.crib && ls.crib.blockedByZone) ? 0 : (ls.crib ? (ls.crib.signed ?? 0) : 0);
      const isTeam1Crib = (cribRecipient === 'p1' || cribRecipient === 'p3' || cribRecipient === 'team1');

      if (isTeam1Crib) t1Crib = cribCounted;
      else t2Crib = cribCounted;

      const q = state.showQueue || [];
      q.forEach(key => {
        if (key === 'crib') return;
        const pack = ls[key];
        if (!pack) return;
        const pts = pack.blockedByZone ? 0 : (pack.score?.total || 0);
        const who = pack.who || key;
        if (who === 'p1' || who === 'p3' || who === 'team1') {
          t1Hand += pts;
        } else {
          t2Hand += pts;
        }
      });

      const t1Round = t1Peg + t1Hand + t1Crib;
      const t2Round = t2Peg + t2Hand + t2Crib;

      handRecord.scores['team1'] = {
        id: 'team1',
        name: pName('team1') || 'Team 1',
        isDealer: isTeam1Crib,
        pegged: t1Peg,
        hand: t1Hand,
        crib: t1Crib,
        hasCrib: isTeam1Crib,
        roundTotal: t1Round,
        runningTotal: state.scores ? (state.scores.team1 || 0) : 0
      };

      handRecord.scores['team2'] = {
        id: 'team2',
        name: pName('team2') || 'Team 2',
        isDealer: !isTeam1Crib,
        pegged: t2Peg,
        hand: t2Hand,
        crib: t2Crib,
        hasCrib: !isTeam1Crib,
        roundTotal: t2Round,
        runningTotal: state.scores ? (state.scores.team2 || 0) : 0
      };
    } else {
      // Individual multiplayer (3+ players)
      const activeSeats = (typeof window !== 'undefined' && typeof window.getActiveSeats === 'function')
        ? window.getActiveSeats()
        : [{ id: 'p1', name: 'Seat 1' }, { id: 'p2', name: 'Seat 2' }, { id: 'p3', name: 'Seat 3' }];

      const cribRecipient = (ls.crib && ls.crib.who) || state.dealer;
      const cribCounted = (ls.crib && ls.crib.blockedByZone) ? 0 : (ls.crib ? (ls.crib.signed ?? 0) : 0);

      activeSeats.forEach(s => {
        const sId = s.id;
        const sPeg = (state.handPeg && state.handPeg[sId]) || 0;
        const pack = ls[sId];
        const sHand = pack ? (pack.blockedByZone ? 0 : (pack.score?.total || 0)) : 0;
        const isCribSeat = (cribRecipient === sId);
        const sCrib = isCribSeat ? cribCounted : 0;
        const sRound = sPeg + sHand + sCrib;
        const sTotal = state.scores ? (state.scores[sId] || 0) : 0;

        handRecord.scores[sId] = {
          id: sId,
          name: s.name || pName(sId),
          isDealer: state.dealer === sId,
          pegged: sPeg,
          hand: sHand,
          crib: sCrib,
          hasCrib: isCribSeat,
          roundTotal: sRound,
          runningTotal: sTotal
        };
      });
    }

    state.handRecorded = true;
    HandScoreTracker.hands.push(handRecord);

    try {
      const repMgr = (typeof window !== 'undefined' && window.CrazyCribbage?.GameReplayManager) || GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
      if (repMgr && repMgr.currentReplay) {
        if (ls.pone?._replayRecorded || ls.dealer?._replayRecorded || ls.crib?._replayRecorded) {
          // Already recorded in real-time as each show act finished
          return handRecord;
        }
        if (count === 2 && !isPart) {
          const dealerIsYou = state.dealer === 'you' || state.dealer === 'p1';
          const poneKey = dealerIsYou ? 'cpu' : 'you';
          const dealerKey = dealerIsYou ? 'you' : 'cpu';

          const poneScore = (ls.pone && ls.pone.score) || { total: 0 };
          const dealScore = (ls.dealer && ls.dealer.score) || { total: 0 };
          const cribSigned = ls.crib ? (ls.crib.signed ?? 0) : 0;

          const poneCounted = (ls.pone && ls.pone.blockedByZone) ? 0 : poneScore.total;
          const dealCounted = (ls.dealer && ls.dealer.blockedByZone) ? 0 : dealScore.total;
          const cribCounted = (ls.crib && ls.crib.blockedByZone) ? 0 : cribSigned;

          const cribRecipient = (ls.crib && ls.crib.who) || dealerKey;
          const youGotCrib = (cribRecipient === 'you' || cribRecipient === 'p1');

          const youEnd = state.scores ? (state.scores.you ?? state.scores.p1 ?? 0) : 0;
          const cpuEnd = state.scores ? (state.scores.cpu ?? state.scores.p2 ?? 0) : 0;

          const youHand = poneKey === 'you' ? poneCounted : dealCounted;
          const cpuHand = poneKey === 'cpu' ? poneCounted : dealCounted;
          const youCrib = youGotCrib ? cribCounted : 0;
          const cpuCrib = !youGotCrib ? cribCounted : 0;

          const youPreShow = youEnd - youHand - youCrib;
          const cpuPreShow = cpuEnd - cpuHand - cpuCrib;

          // Progressive score after pone counts
          const p1AfterPone = poneKey === 'you' ? (youPreShow + poneCounted) : youPreShow;
          const p2AfterPone = poneKey === 'cpu' ? (cpuPreShow + poneCounted) : cpuPreShow;

          repMgr.recordEvent('show_hand', {
            round: handNum,
            who: poneKey === 'you' ? 'p1' : 'p2',
            starter: state.starter,
            cards: ls.pone?.cards || [],
            points: poneCounted,
            combos: ls.pone?.score?.lines || ls.pone?.score?.items || ls.pone?.score?.notes || [],
            scores: { p1: p1AfterPone, p2: p2AfterPone }
          });

          // Progressive score after dealer counts
          const p1AfterDeal = p1AfterPone + (dealerKey === 'you' ? dealCounted : 0);
          const p2AfterDeal = p2AfterPone + (dealerKey === 'cpu' ? dealCounted : 0);

          repMgr.recordEvent('show_hand', {
            round: handNum,
            who: dealerKey === 'you' ? 'p1' : 'p2',
            starter: state.starter,
            cards: ls.dealer?.cards || [],
            points: dealCounted,
            combos: ls.dealer?.score?.lines || ls.dealer?.score?.items || ls.dealer?.score?.notes || [],
            scores: { p1: p1AfterDeal, p2: p2AfterDeal }
          });

          if (cribCounted > 0 || (state.crib && state.crib.length) || (ls.crib && ls.crib.cards)) {
            // Progressive score after crib counts (matches final end scores)
            const p1AfterCrib = p1AfterDeal + (youGotCrib ? cribCounted : 0);
            const p2AfterCrib = p2AfterDeal + (!youGotCrib ? cribCounted : 0);

            repMgr.recordEvent('show_crib', {
              round: handNum,
              who: youGotCrib ? 'p1' : 'p2',
              starter: state.starter,
              cards: state.crib || ls.crib?.cards || [],
              points: cribCounted,
              combos: ls.crib?.score?.lines || ls.crib?.score?.items || ls.crib?.score?.notes || [],
              scores: { p1: p1AfterCrib, p2: p2AfterCrib }
            });
          }
        } else {
          // Multiplayer / Teams show queue
          const q = state.showQueue || Object.keys(ls);
          for (const key of q) {
            if (key === 'crib') {
              const pack = ls.crib;
              const cribPts = pack ? (pack.blockedByZone ? 0 : (pack.signed ?? pack.score?.total ?? 0)) : 0;
              repMgr.recordEvent('show_crib', {
                round: handNum,
                who: pack?.who || state.dealer || 'p1',
                starter: state.starter,
                cards: pack?.cards || state.crib || [],
                points: cribPts,
                combos: pack?.score?.lines || pack?.score?.items || pack?.score?.notes || [],
                scores: state.scores
              });
            } else if (ls[key]) {
              const pack = ls[key];
              const handPts = pack ? (pack.blockedByZone ? 0 : (pack.score?.total || 0)) : 0;
              repMgr.recordEvent('show_hand', {
                round: handNum,
                who: pack.who || key,
                starter: state.starter,
                cards: pack.cards || [],
                points: handPts,
                combos: pack.score?.lines || pack.score?.items || pack.score?.notes || [],
                scores: state.scores
              });
            }
          }
        }
      }
    } catch (_) {}

    return handRecord;
  }

  /**
   * If game ended during pegging (before show phase), records the final partial hand.
   * @param {Object} state
   * @param {Object} settings
   * @param {string} [winner]
   * @param {Object} [options]
   */
  static recordFinalPartialHand(state, settings = {}, winner = null, options = {}) {
    if (!state || state.handRecorded) return null;
    const pName = options.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (s => s === 'you' ? 'You' : s === 'cpu' ? 'Computer' : s));
    const isPart = options.isPartnership || (typeof window !== 'undefined' && typeof window.isPartnership === 'function' ? window.isPartnership() : false);
    const count = settings.playerCount || 2;
    const handNum = HandScoreTracker.hands.length + 1;

    const handRecord = {
      handNumber: handNum,
      dealer: state.dealer,
      dealerName: pName(state.dealer),
      starter: state.starter ? { ...state.starter } : null,
      scores: {},
      isPartnership: isPart,
      endedMidPlay: true
    };

    if (count === 2 && !isPart) {
      const youPeg = (state.handPeg && (state.handPeg.you ?? state.handPeg.p1)) || 0;
      const cpuPeg = (state.handPeg && (state.handPeg.cpu ?? state.handPeg.p2)) || 0;
      const dealerIsYou = state.dealer === 'you' || state.dealer === 'p1';

      handRecord.scores['p1'] = {
        id: 'p1',
        name: pName('you'),
        isDealer: dealerIsYou,
        pegged: youPeg,
        hand: 0,
        crib: 0,
        hasCrib: dealerIsYou,
        roundTotal: youPeg,
        runningTotal: state.scores ? (state.scores.you ?? state.scores.p1 ?? 0) : 0
      };

      handRecord.scores['p2'] = {
        id: 'p2',
        name: pName('cpu'),
        isDealer: !dealerIsYou,
        pegged: cpuPeg,
        hand: 0,
        crib: 0,
        hasCrib: !dealerIsYou,
        roundTotal: cpuPeg,
        runningTotal: state.scores ? (state.scores.cpu ?? state.scores.p2 ?? 0) : 0
      };
    } else if (isPart) {
      const t1Peg = (state.handPeg && state.handPeg.team1) || 0;
      const t2Peg = (state.handPeg && state.handPeg.team2) || 0;
      const isTeam1Dealer = (state.dealer === 'p1' || state.dealer === 'p3' || state.dealer === 'team1');

      handRecord.scores['team1'] = {
        id: 'team1',
        name: pName('team1') || 'Team 1',
        isDealer: isTeam1Dealer,
        pegged: t1Peg,
        hand: 0,
        crib: 0,
        hasCrib: isTeam1Dealer,
        roundTotal: t1Peg,
        runningTotal: state.scores ? (state.scores.team1 || 0) : 0
      };

      handRecord.scores['team2'] = {
        id: 'team2',
        name: pName('team2') || 'Team 2',
        isDealer: !isTeam1Dealer,
        pegged: t2Peg,
        hand: 0,
        crib: 0,
        hasCrib: !isTeam1Dealer,
        roundTotal: t2Peg,
        runningTotal: state.scores ? (state.scores.team2 || 0) : 0
      };
    } else {
      const activeSeats = (typeof window !== 'undefined' && typeof window.getActiveSeats === 'function')
        ? window.getActiveSeats()
        : [{ id: 'p1', name: 'Seat 1' }, { id: 'p2', name: 'Seat 2' }, { id: 'p3', name: 'Seat 3' }];

      activeSeats.forEach(s => {
        const sId = s.id;
        const sPeg = (state.handPeg && state.handPeg[sId]) || 0;
        handRecord.scores[sId] = {
          id: sId,
          name: s.name || pName(sId),
          isDealer: state.dealer === sId,
          pegged: sPeg,
          hand: 0,
          crib: 0,
          hasCrib: state.dealer === sId,
          roundTotal: sPeg,
          runningTotal: state.scores ? (state.scores[sId] || 0) : 0
        };
      });
    }

    state.handRecorded = true;
    HandScoreTracker.hands.push(handRecord);
    return handRecord;
  }

  /**
   * Calculates overall aggregate statistics across all recorded hands.
   * @returns {Object}
   */
  static getStats() {
    const hands = HandScoreTracker.hands;
    if (!hands.length) {
      return { totalHands: 0, participants: {} };
    }

    const participants = {};
    const firstHand = hands[0];
    Object.keys(firstHand.scores).forEach(key => {
      participants[key] = {
        name: firstHand.scores[key].name,
        totalPegged: 0,
        totalHand: 0,
        totalCrib: 0,
        totalRound: 0,
        highestHand: 0,
        highestPeg: 0,
        finalScore: 0
      };
    });

    hands.forEach(h => {
      Object.keys(h.scores).forEach(key => {
        if (!participants[key]) {
          participants[key] = {
            name: h.scores[key].name,
            totalPegged: 0,
            totalHand: 0,
            totalCrib: 0,
            totalRound: 0,
            highestHand: 0,
            highestPeg: 0,
            finalScore: 0
          };
        }
        const s = h.scores[key];
        const p = participants[key];
        p.totalPegged += (s.pegged || 0);
        p.totalHand += (s.hand || 0);
        p.totalCrib += (s.crib || 0);
        p.totalRound += (s.roundTotal || 0);
        if ((s.hand || 0) > p.highestHand) p.highestHand = s.hand;
        if ((s.pegged || 0) > p.highestPeg) p.highestPeg = s.pegged;
        p.finalScore = s.runningTotal || 0;
      });
    });

    return {
      totalHands: hands.length,
      participants
    };
  }

  /**
   * Builds the complete scorecard table and summary modal HTML.
   * @param {Object} [context]
   * @returns {string} HTML string for modal display
   */
  static buildScorecardHtml(context = {}) {
    const hands = HandScoreTracker.hands;
    const state = context.state || (typeof window !== 'undefined' && window.state ? window.state : {});
    const settings = context.settings || (typeof window !== 'undefined' && window.settings ? window.settings : {});
    const target = settings.target || 121;
    const isGameOver = Boolean(state && state.over);

    if (!hands.length) {
      return `
        <div class="scorecard-wrap">
          <div class="scorecard-header">
            <h2>📊 Game Scorecard &amp; Summary</h2>
            <p class="sc-subtitle">Target: ${target} pts · High Ball</p>
          </div>
          <div class="sc-empty-state">
            <div style="font-size:2.8rem;margin-bottom:8px;">🃏</div>
            <p style="font-weight:700;color:var(--gold,#f0c64a);margin-bottom:6px;">No Completed Hands Yet</p>
            <p style="color:#d7e8dc;font-size:0.92rem;line-height:1.5;">Scores and points will be tracked automatically hand-by-hand as pegging and hands are completed.</p>
          </div>
          <div class="sc-actions sc-actions-centered">
            <button type="button" class="btn-gold sc-btn sc-btn-back modal-btn-close" id="btnCloseScorecardEmpty">✕ Close Scorecard</button>
          </div>
        </div>
      `;
    }

    const stats = HandScoreTracker.getStats();
    const pKeys = Object.keys(hands[0].scores);

    // Build stat badges
    let bestHandText = '—';
    let bestHandPts = -1;
    let bestHandPlayer = '';
    pKeys.forEach(k => {
      const p = stats.participants[k];
      if (p && p.highestHand > bestHandPts) {
        bestHandPts = p.highestHand;
        bestHandPlayer = p.name;
      }
    });
    if (bestHandPts >= 0) {
      bestHandText = `${bestHandPts} pts <span class="sc-badge-sub">(${bestHandPlayer})</span>`;
    }

    const topPegPlayer = pKeys.reduce((best, k) => {
      const cur = stats.participants[k]?.totalPegged || 0;
      return cur > (best.pts || 0) ? { name: stats.participants[k]?.name, pts: cur } : best;
    }, { name: '', pts: -1 });

    const badgesHtml = `
      <div class="sc-badge-grid">
        <div class="sc-stat-pill">
          <span class="sc-stat-label">Hands Played</span>
          <span class="sc-stat-val">${stats.totalHands}</span>
        </div>
        <div class="sc-stat-pill ${bestHandPts >= 12 ? 'clickable-stat-pill' : ''}" ${bestHandPts >= 12 ? `data-action="show-glory" style="cursor:pointer;" title="Click to view &amp; share Hand of Glory card"` : ''}>
          <span class="sc-stat-label">Top Hand ${bestHandPts >= 12 ? '🏆' : ''}</span>
          <span class="sc-stat-val gold">${bestHandText}</span>
        </div>
        <div class="sc-stat-pill">
          <span class="sc-stat-label">Peg Leader</span>
          <span class="sc-stat-val">${topPegPlayer.name} (${topPegPlayer.pts})</span>
        </div>
      </div>
    `;

    // Table Header
    let playerColsHeader = '';
    let subHeaders = '';
    pKeys.forEach((k, idx) => {
      const p = stats.participants[k];
      const isP1 = idx === 0;
      let avatarHtml = '';
      if (isP1) {
        const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
        if (avMgr && typeof avMgr.getSelectedAvatarId === 'function') {
          avatarHtml = `<span style="vertical-align:middle;margin-right:4px;">${avMgr.renderAvatarBadge(avMgr.getSelectedAvatarId(), { size: 18, showRing: false })}</span>`;
        }
      }
      playerColsHeader += `<th colspan="5" class="sc-th-player ${isP1 ? 'sc-p1-col' : 'sc-p2-col'}">${avatarHtml}${p ? p.name : k}</th>`;
      subHeaders += `
        <th class="sc-th-sub" title="Pegging Points">Peg</th>
        <th class="sc-th-sub" title="Hand Score">Hand</th>
        <th class="sc-th-sub" title="Crib Score">Crib</th>
        <th class="sc-th-sub sc-rnd" title="Round Total">Rnd</th>
        <th class="sc-th-sub sc-tot" title="Running Match Total">Total</th>
      `;
    });

    // Table Rows
    const rowsHtml = hands.map(h => {
      const cutHtml = HandScoreTracker.formatMiniCard(h.starter);
      let playerCells = '';

      pKeys.forEach((k, idx) => {
        const s = h.scores[k] || { pegged: 0, hand: 0, crib: 0, roundTotal: 0, runningTotal: 0, isDealer: false };
        const isDealer = s.isDealer;
        const dealerCrown = isDealer ? '<span class="sc-dealer-icon" title="Dealer">👑</span>' : '';
        const cribDisplay = s.hasCrib ? `+${s.crib}` : '<span style="opacity:0.35;">—</span>';
        const midPlayTag = h.endedMidPlay ? ' <span class="sc-midplay" title="Game ended during pegging">*</span>' : '';

        playerCells += `
          <td class="sc-num sc-peg">+${s.pegged}</td>
          <td class="sc-num sc-hand">${h.endedMidPlay ? '—' : s.hand}</td>
          <td class="sc-num sc-crib">${cribDisplay}</td>
          <td class="sc-num sc-round"><strong>${s.roundTotal}</strong>${midPlayTag}</td>
          <td class="sc-num sc-running"><span class="sc-total-pill">${s.runningTotal}</span></td>
        `;
      });

      return `
        <tr>
          <td class="sc-hand-col">
            <span class="sc-hand-num">${h.handNumber}</span>
            <span class="sc-dealer-cell" title="Dealer: ${h.dealerName}">👑 ${h.dealerName}</span>
          </td>
          <td class="sc-cut-col">${cutHtml}</td>
          ${playerCells}
        </tr>
      `;
    }).join('');

    // Table Footer Totals
    let footerCells = '';
    pKeys.forEach(k => {
      const p = stats.participants[k] || { totalPegged: 0, totalHand: 0, totalCrib: 0, totalRound: 0, finalScore: 0 };
      footerCells += `
        <td class="sc-num sc-peg"><strong>${p.totalPegged}</strong></td>
        <td class="sc-num sc-hand"><strong>${p.totalHand}</strong></td>
        <td class="sc-num sc-crib"><strong>${p.totalCrib}</strong></td>
        <td class="sc-num sc-round"><strong>${p.totalRound}</strong></td>
        <td class="sc-num sc-running"><strong class="sc-final-pill">${p.finalScore}</strong></td>
      `;
    });

    const isTournament = Boolean(
      (settings && settings.isTournamentMatch) ||
      (context && (context.isTournament || context.isTournamentMatch)) ||
      (typeof window !== 'undefined' && window.settings && window.settings.isTournamentMatch)
    );

    const actionBtn = (isGameOver && !isTournament)
      ? `<button type="button" class="sc-btn sc-btn-rematch" id="btnScorecardRematch" data-action="rematch">🔁 Rematch</button>`
      : (isGameOver && isTournament
        ? `<button type="button" class="sc-btn sc-btn-tourney" id="btnScorecardTourney" data-action="tourney">🏆 Tournament Bracket</button>`
        : '');

    const coachBtn = isGameOver
      ? `<button type="button" class="sc-btn sc-btn-coach" id="btnScorecardCoach" data-action="coach">🎓 Coach's Post-Mortem</button>`
      : '';

    const backBtnText = isGameOver ? '🏆 Back to Game Results' : '✕ Close Scorecard';

    return `
      <div class="scorecard-wrap">
        <div class="scorecard-header">
          <h2>📊 Game Scorecard &amp; Summary</h2>
          <p class="sc-subtitle">Target: ${target} pts · High Ball · ${hands.length} Hand${hands.length === 1 ? '' : 's'} Logged</p>
        </div>
        ${badgesHtml}
        <div class="sc-table-container">
          <table class="scorecard-table">
            <thead>
              <tr class="sc-top-header">
                <th rowspan="2" class="sc-th-hand">Hand</th>
                <th rowspan="2" class="sc-th-cut">Cut</th>
                ${playerColsHeader}
              </tr>
              <tr class="sc-sub-header">
                ${subHeaders}
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
            <tfoot>
              <tr class="sc-totals-row">
                <td colspan="2" class="sc-totals-label">TOTALS</td>
                ${footerCells}
              </tr>
            </tfoot>
          </table>
        </div>
        <div style="font-size:0.78rem;color:#d7e8dc;opacity:0.75;margin:8px 0 14px;text-align:center;">
          👑 = Dealer for the hand &nbsp;·&nbsp; Running total tracks cumulative board score after each hand
        </div>
        <div class="sc-actions${(!coachBtn && !actionBtn) ? ' sc-actions-centered' : ''}">
          ${(coachBtn || actionBtn) ? `
            <div class="sc-actions-tools">
              ${coachBtn}
              ${actionBtn}
            </div>
          ` : ''}
          <div class="sc-actions-main">
            <button type="button" class="btn-gold sc-btn sc-btn-back modal-btn-close" id="btnCloseScorecard" data-action="close">${backBtnText}</button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Binds click event listeners for elements inside a scorecard container.
   * @param {Element|Document} [root=document]
   */
  static bindEvents(root = (typeof document !== 'undefined' ? document : null)) {
    if (!root || typeof root.querySelector !== 'function') return;
    const rematchBtn = root.querySelector('#btnScorecardRematch, [data-action="rematch"]');
    if (rematchBtn && !rematchBtn._boundScorecard) {
      rematchBtn._boundScorecard = true;
      rematchBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.playAgain === 'function') {
          window.playAgain();
        }
      });
    }
    const tourneyBtn = root.querySelector('#btnScorecardTourney, [data-action="tourney"]');
    if (tourneyBtn && !tourneyBtn._boundScorecard) {
      tourneyBtn._boundScorecard = true;
      tourneyBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.hideModal === 'function') window.hideModal();
        if (typeof window !== 'undefined' && window.TournamentModal && typeof window.TournamentModal.open === 'function') {
          window.TournamentModal.open();
        }
      });
    }
    const coachBtn = root.querySelector('#btnScorecardCoach, [data-action="coach"]');
    if (coachBtn && !coachBtn._boundScorecard) {
      coachBtn._boundScorecard = true;
      coachBtn.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.showCoachPostMortemModal === 'function') {
          window.showCoachPostMortemModal();
        } else if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showCoachPostMortemModal === 'function') {
          window.modalManager.showCoachPostMortemModal();
        }
      });
    }
    const gloryPill = root.querySelector('.clickable-stat-pill, [data-action="show-glory"]');
    if (gloryPill && !gloryPill._boundScorecard) {
      gloryPill._boundScorecard = true;
      gloryPill.addEventListener('click', () => {
        if (typeof window !== 'undefined' && typeof window.showHandOfGloryModal === 'function') {
          window.showHandOfGloryModal('best');
        }
      });
    }
    const closeBtn = root.querySelector('#btnCloseScorecard');
    if (closeBtn && !closeBtn._boundScorecard) {
      closeBtn._boundScorecard = true;
      closeBtn.addEventListener('click', (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function' && window.showGameWonModal()) {
          return;
        } else if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showGameWonModal === 'function' && window.modalManager.showGameWonModal()) {
          return;
        } else if (typeof window !== 'undefined' && typeof window.hideModal === 'function') {
          window.hideModal();
        }
      });
    }
    const emptyCloseBtn = root.querySelector('#btnCloseScorecardEmpty');
    if (emptyCloseBtn && !emptyCloseBtn._boundScorecard) {
      emptyCloseBtn._boundScorecard = true;
      emptyCloseBtn.addEventListener('click', (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (typeof window !== 'undefined' && typeof window.hideModal === 'function') {
          window.hideModal();
        }
      });
    }
  }
}
